import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import { SelectMenu } from 'src/components/SelectMenu';
import { SelectMenuOption } from 'src/components/SelectMenu/select-menu.types';
import {
  ORDERED_SLA_THRESHOLDS,
  SLA_THRESHOLD_LABELS,
  SlaThresholdMap,
  TimeBasedRoutingMap,
  VoiceRoutingAction,
} from 'src/pages/Settings/preferences/hooks/sla-routing.constants';

const RoutingAccordion = styled(Accordion)(() => ({
  backgroundColor: 'transparent',
  backgroundImage: 'none',
  borderRadius: 0,
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
}));

const RoutingAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(1.5, 0),
  minHeight: 'auto',
  flexDirection: 'row-reverse',
  gap: theme.spacing(1),
  '& .MuiAccordionSummary-content': {
    margin: 0,
    alignItems: 'center',
  },
  '&:hover .MuiTypography-root': {
    color: theme.palette.custom.OV.sys.secondary,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.text.secondary,
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
}));

const RoutingAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 0, 1.75),
}));

interface AssigneeGroupOption {
  id: string;
  value: string;
  label: string;
}

interface SlaRoutingConfigProps {
  title: string;
  routingMap: TimeBasedRoutingMap | undefined;
  onUpdate: (
    timeBlock: 'during_business_hours' | 'after_business_hours',
    thresholdMap: SlaThresholdMap,
  ) => void;
  mode: 'email' | 'voice';
  assigneeGroupOptions?: AssigneeGroupOption[];
  defaultExpanded?: boolean;
}

const VOICE_ROUTING_OPTIONS: { value: VoiceRoutingAction | ''; label: string }[] = [
  { value: '', label: 'Default' },
  { value: 'route_to_provision', label: 'Route to Basic' },
  { value: 'connect_to_partner', label: 'Route to Partner' },
];

const EMPTY_ASSIGNEE_GROUP_OPTIONS: AssigneeGroupOption[] = [];

const gridHeaderSx: SxProps<Theme> = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'custom.OV.sys.onSurfaceVariant',
};

const routingGridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns:
    'minmax(6.875rem, 1fr) minmax(9.375rem, 1.5fr) minmax(9.375rem, 1.5fr)',
  columnGap: 1.75,
  rowGap: 1.25,
  alignItems: 'center',
};

const channelTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: '0.78rem',
  letterSpacing: '0.02em',
  color: 'custom.OV.sys.onSurface',
  transition: (theme) =>
    theme.transitions.create('color', {
      duration: theme.transitions.duration.shortest,
    }),
};

export const SlaRoutingConfig = ({
  title,
  routingMap,
  onUpdate,
  mode,
  assigneeGroupOptions = EMPTY_ASSIGNEE_GROUP_OPTIONS,
  defaultExpanded = false,
}: SlaRoutingConfigProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));

  const assigneeGroupOptionsWithDefault = useMemo(
    () => [{ id: '', value: '', label: 'Default' }, ...assigneeGroupOptions],
    [assigneeGroupOptions],
  );

  const handleChange = useCallback(
    (
      timeBlock: 'during_business_hours' | 'after_business_hours',
      threshold: string,
      value: string,
    ) => {
      const currentMap = routingMap?.[timeBlock] ?? {};
      const newMap: SlaThresholdMap = {
        ...currentMap,
        [threshold]: value || null,
      };
      onUpdate(timeBlock, newMap);
    },
    [routingMap, onUpdate],
  );

  const selectMenuOptions = useMemo<SelectMenuOption[]>(() => {
    const source =
      mode === 'voice' ? VOICE_ROUTING_OPTIONS : assigneeGroupOptionsWithDefault;
    return source.map((opt) => ({ value: opt.value, title: opt.label }));
  }, [mode, assigneeGroupOptionsWithDefault]);

  const renderSelect = (
    timeBlock: 'during_business_hours' | 'after_business_hours',
    threshold: string,
  ) => {
    const currentValue = routingMap?.[timeBlock]?.[threshold] ?? '';

    return (
      <SelectMenu
        label="SLA routing"
        options={selectMenuOptions}
        value={currentValue}
        onChange={(value) => handleChange(timeBlock, threshold, value)}
        menuWidth="12rem"
        sx={{ width: '100%' }}
      />
    );
  };

  const compactRows = (
    <Stack spacing={2} pt={0.5}>
      {ORDERED_SLA_THRESHOLDS.map((threshold) => (
        <Box key={threshold}>
          <Typography variant="body2" fontWeight={600} mb={0.5}>
            {SLA_THRESHOLD_LABELS[threshold]}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="text.secondary">
                During Hours
              </Typography>
              {renderSelect('during_business_hours', threshold)}
            </Box>
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="text.secondary">
                After Hours
              </Typography>
              {renderSelect('after_business_hours', threshold)}
            </Box>
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  const routingGrid = (
    <Box sx={routingGridSx}>
      <Typography component="span" sx={gridHeaderSx}>
        SLA Tier
      </Typography>
      <Typography component="span" sx={gridHeaderSx}>
        During Hours
      </Typography>
      <Typography component="span" sx={gridHeaderSx}>
        After Hours
      </Typography>
      {ORDERED_SLA_THRESHOLDS.map((threshold) => (
        <Box key={threshold} sx={{ display: 'contents' }}>
          <Typography variant="body2" fontWeight={600}>
            {SLA_THRESHOLD_LABELS[threshold]}
          </Typography>
          {renderSelect('during_business_hours', threshold)}
          {renderSelect('after_business_hours', threshold)}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        '&:not(:first-of-type)': {
          borderTop: '1px solid',
          borderColor: 'custom.OV.sys.outlineVariant',
        },
      }}
    >
      <RoutingAccordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        disableGutters
      >
        <RoutingAccordionSummary expandIcon={<ChevronRightIcon fontSize="small" />}>
          <Typography variant="body2" sx={channelTitleSx}>
            {title}
          </Typography>
        </RoutingAccordionSummary>
        <RoutingAccordionDetails>{isCompact ? compactRows : routingGrid}</RoutingAccordionDetails>
      </RoutingAccordion>
    </Box>
  );
};
